import { useEffect, useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

const ComplaintsPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const details = (form.elements.namedItem('complaint_details') as HTMLTextAreaElement)?.value || '';
    if (details.length > 500) return;
    setSubmitting(true);
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });
    try {
      await fetch('https://readdy.ai/api/form/d7bqqcqqofe8vli40npg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    {
      step: '01',
      title: 'Contact Us Directly',
      desc: 'In the first instance, please contact our customer service team by phone or email. Many issues can be resolved quickly at this stage.',
      icon: 'ri-phone-line',
    },
    {
      step: '02',
      title: 'Formal Complaint Acknowledgement',
      desc: 'If your issue is not resolved to your satisfaction, submit a formal complaint. We will acknowledge receipt within 2 working days.',
      icon: 'ri-mail-check-line',
    },
    {
      step: '03',
      title: 'Investigation',
      desc: 'A senior member of our team will investigate your complaint thoroughly, reviewing all records, reports, and communications.',
      icon: 'ri-search-eye-line',
    },
    {
      step: '04',
      title: 'Full Response',
      desc: 'We will provide a full written response within 14 working days of receiving your formal complaint, outlining our findings and proposed resolution.',
      icon: 'ri-file-list-3-line',
    },
    {
      step: '05',
      title: 'Escalation',
      desc: 'If you remain dissatisfied with our response, you may escalate your complaint to our Managing Director for a final review.',
      icon: 'ri-arrow-up-circle-line',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gray-900 py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-orange-400 text-sm mb-8 transition-colors cursor-pointer">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 flex items-center justify-center bg-orange-500/20 rounded-xl">
              <i className="ri-customer-service-2-line text-orange-400 text-2xl"></i>
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white">Complaints Procedure</h1>
              <p className="text-gray-400 text-sm mt-1">Elite Drainage Services Ltd — How We Handle Complaints</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-16">

        {/* Intro */}
        <div className="mb-14">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            At Elite Drainage Services Ltd, we are committed to providing a high standard of service to all our customers. We take all complaints seriously and aim to resolve them fairly, promptly, and professionally.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            If you are unhappy with any aspect of our service, please follow the procedure below. Your feedback helps us improve.
          </p>
        </div>

        {/* Contact Details */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-14">
          {[
            { icon: 'ri-phone-line', label: 'Phone', value: '08000487055', sub: 'Mon–Fri 8am–6pm' },
            { icon: 'ri-mail-line', label: 'Email', value: 'info@elitedrain-services.com', sub: 'Complaints team' },
            { icon: 'ri-time-line', label: 'Response Time', value: '2 working days', sub: 'Acknowledgement' },
          ].map((item) => (
            <div key={item.label} className="bg-gray-50 rounded-2xl p-5 flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-xl flex-shrink-0">
                <i className={`${item.icon} text-orange-500 text-lg`}></i>
              </div>
              <div>
                <div className="text-gray-400 text-xs uppercase tracking-wide font-semibold mb-1">{item.label}</div>
                <div className="text-gray-900 font-bold text-sm">{item.value}</div>
                <div className="text-gray-400 text-xs mt-0.5">{item.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Process Steps */}
        <h2 className="text-2xl font-extrabold text-gray-900 mb-8">Our Complaints Process</h2>
        <div className="space-y-4 mb-14">
          {steps.map((s) => (
            <div key={s.step} className="flex items-start gap-5 bg-gray-50 rounded-2xl p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-500 rounded-xl flex-shrink-0">
                <span className="text-white font-extrabold text-sm">{s.step}</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${s.icon} text-orange-500 text-base`}></i>
                  </div>
                  <h3 className="text-gray-900 font-bold text-base">{s.title}</h3>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Timeframes */}
        <div className="bg-gray-900 rounded-2xl p-8 mb-14">
          <h2 className="text-white font-bold text-xl mb-6">Response Timeframes</h2>
          <div className="space-y-4">
            {[
              { stage: 'Acknowledgement of formal complaint', time: 'Within 2 working days' },
              { stage: 'Initial response / update', time: 'Within 5 working days' },
              { stage: 'Full written response', time: 'Within 14 working days' },
              { stage: 'Escalation to Managing Director', time: 'Within 7 working days of escalation' },
              { stage: 'Final response (all stages exhausted)', time: 'Within 28 working days total' },
            ].map((row) => (
              <div key={row.stage} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-800 pb-4 last:border-0 last:pb-0">
                <span className="text-gray-300 text-sm">{row.stage}</span>
                <span className="text-orange-400 font-semibold text-sm whitespace-nowrap">{row.time}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Escalation */}
        <div className="mb-14">
          <h2 className="text-2xl font-extrabold text-gray-900 mb-4">Escalation Procedure</h2>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            If you are not satisfied with our final response, you may escalate your complaint as follows:
          </p>
          <div className="space-y-4">
            <div className="border border-gray-100 rounded-2xl p-5">
              <h3 className="text-gray-900 font-bold text-sm mb-2">Step 1 — Managing Director Review</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Request a review by our Managing Director by writing to <strong>info@elitedrain-services.com</strong> with the subject line "MD Escalation — [Your Name]". Include your original complaint reference number.
              </p>
            </div>
            <div className="border border-gray-100 rounded-2xl p-5">
              <h3 className="text-gray-900 font-bold text-sm mb-2">Step 2 — Alternative Dispute Resolution</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                If you remain dissatisfied after our final response, you may refer your complaint to an independent Alternative Dispute Resolution (ADR) scheme. We will provide details of the relevant ADR scheme in our final response letter.
              </p>
            </div>
            <div className="border border-gray-100 rounded-2xl p-5">
              <h3 className="text-gray-900 font-bold text-sm mb-2">Step 3 — Trading Standards / Citizens Advice</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                You also have the right to contact Citizens Advice (0808 223 1133) or your local Trading Standards office for independent guidance at any stage of the complaints process.
              </p>
            </div>
          </div>
        </div>

        {/* Complaint Form */}
        <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
          <h2 className="text-gray-900 font-bold text-xl mb-2">Submit a Formal Complaint</h2>
          <p className="text-gray-500 text-sm mb-6">Use this form to submit a formal complaint. We will acknowledge receipt within 2 working days.</p>

          {submitted ? (
            <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full">
                <i className="ri-check-line text-green-500 text-3xl"></i>
              </div>
              <h3 className="text-gray-900 font-bold text-xl">Complaint Received</h3>
              <p className="text-gray-500 text-sm max-w-xs">
                Thank you for submitting your complaint. We will acknowledge receipt within 2 working days and keep you updated throughout the process.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-4 text-orange-500 font-semibold text-sm cursor-pointer hover:underline"
              >
                Submit another complaint
              </button>
            </div>
          ) : (
            <form
              data-readdy-form
              id="complaints-form"
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-600 text-xs font-semibold uppercase tracking-wide block mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    name="full_name"
                    required
                    placeholder="Your full name"
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-gray-600 text-xs font-semibold uppercase tracking-wide block mb-1.5">Account / Reference Number</label>
                  <input
                    type="text"
                    name="account_number"
                    placeholder="If known"
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="text-gray-600 text-xs font-semibold uppercase tracking-wide block mb-1.5">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="your@email.com"
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
                />
              </div>
              <div>
                <label className="text-gray-600 text-xs font-semibold uppercase tracking-wide block mb-1.5">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="07700 900000"
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
                />
              </div>
              <div>
                <label className="text-gray-600 text-xs font-semibold uppercase tracking-wide block mb-1.5">Nature of Complaint *</label>
                <select
                  name="complaint_type"
                  required
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-orange-400 transition-colors cursor-pointer"
                >
                  <option value="">Select complaint type...</option>
                  <option value="service_quality">Quality of service / workmanship</option>
                  <option value="response_time">Response time / delays</option>
                  <option value="billing">Billing or payment dispute</option>
                  <option value="claim_rejected">Claim rejected / coverage dispute</option>
                  <option value="engineer_conduct">Engineer conduct or behaviour</option>
                  <option value="cancellation">Cancellation issue</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-gray-600 text-xs font-semibold uppercase tracking-wide block mb-1.5">Complaint Details *</label>
                <textarea
                  name="complaint_details"
                  rows={5}
                  required
                  maxLength={500}
                  placeholder="Please describe your complaint in as much detail as possible, including dates, engineer names, and any previous contact with our team..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors resize-none"
                ></textarea>
                <div className="text-right text-xs text-gray-400 mt-1">{charCount}/500</div>
              </div>
              <div>
                <label className="text-gray-600 text-xs font-semibold uppercase tracking-wide block mb-1.5">Desired Resolution</label>
                <input
                  type="text"
                  name="desired_resolution"
                  placeholder="What outcome would you like?"
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-bold text-sm py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap mt-2"
              >
                {submitting ? 'Submitting...' : 'Submit Formal Complaint'}
              </button>
              <p className="text-gray-400 text-xs text-center">
                We will acknowledge your complaint within 2 working days. Your data is handled in accordance with our Privacy Policy.
              </p>
            </form>
          )}
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-3 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ComplaintsPage;
